import { CrudPage, type Field, type Column } from "../components/CrudPage";

const STATUSES = ["DRAFT", "ACTIVE", "PAUSED", "COMPLETED", "CANCELLED"] as const;
const RATE_UNITS = ["hour", "visit", "month", "sqft", "door", "percent"] as const;
const SERVICE_LINES = ["cleaning", "property-management"] as const;

/** Slugs are what the API stores; these are what a person should read. */
const SERVICE_LINE_LABELS = {
  cleaning: "Cleaning",
  "property-management": "Property management",
} as const;

/**
 * A customer and the work for them, in one record.
 *
 * The commercial terms are not required: a customer imported from QuickBooks has
 * a billing history and no rate on file anywhere, and demanding one would have
 * meant inventing it. The money fields are absent from the form for the opposite
 * reason -- they are a rollup QuickBooks owns, and the next import would
 * overwrite anything typed here.
 */
const fields: Field[] = [
  { name: "clientName", label: "Customer", type: "text", required: true },
  { name: "companyName", label: "Company", type: "text" },
  {
    name: "serviceLine",
    label: "Service line",
    type: "select",
    required: true,
    options: SERVICE_LINES,
    help: "Cleaning work, or a property managed on the owner's behalf.",
  },
  { name: "status", label: "Status", type: "select", required: true, options: STATUSES },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "email", label: "Email", type: "email" },
  { name: "website", label: "Website", type: "url", placeholder: "https://…" },
  { name: "siteAddress", label: "Site address", type: "text" },
  { name: "scope", label: "Scope", type: "textarea" },
  { name: "rate", label: "Rate", type: "number" },
  {
    name: "rateUnit",
    label: "Per",
    type: "select",
    options: RATE_UNITS,
    help: "Per door and percent are for managed properties; with percent the rate is the percentage, not dollars.",
  },
  {
    name: "unitCount",
    label: "Units",
    type: "number",
    help: "Doors under management. Property management only.",
  },
  { name: "schedule", label: "Schedule", type: "text", placeholder: "Weekly, Tuesday evenings" },
  { name: "startDate", label: "Start date", type: "date" },
  { name: "endDate", label: "End date", type: "date" },
  { name: "notes", label: "Notes", type: "textarea" },
];

const columns: Column[] = [
  { name: "clientName", label: "Customer" },
  { name: "status", label: "Status", pill: true },
  {
    name: "serviceLine",
    label: "Line",
    labels: SERVICE_LINE_LABELS,
    // Records written before the field existed are cleaning contracts.
    fallback: "cleaning",
  },
  { name: "phone", label: "Phone" },
  { name: "totalBilled", label: "Billed", money: true },
  { name: "openBalance", label: "Owing", money: true },
  { name: "lastInvoiceDate", label: "Last invoice", date: true },
];

export function Contracts() {
  return (
    <CrudPage
      resource="contracts"
      title="Contracts"
      fields={fields}
      columns={columns}
      statusField="status"
      statuses={STATUSES}
      secondaryFilter={{
        field: "serviceLine",
        allLabel: "All service lines",
        fallback: "cleaning",
        options: SERVICE_LINES.map((v) => ({ value: v, label: SERVICE_LINE_LABELS[v] })),
      }}
      emptyHint="Customers you work for — cleaning contracts and managed properties. Import them from QuickBooks, win a lead, or add one by hand."
    />
  );
}
